import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";

import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import { getCompanyId } from "@lhd/utility";
import { Position } from "@lhd/types";
import axiosInstanceV2 from "@lhd/libs/axiosV2";
import { handleMessageError } from "@lhd/features/Apps/utils";

const deletingErrorToastSet = new Set<string>();
const notifyDeleteErrorOnce = (message: string) => {
  if (deletingErrorToastSet.has(message)) return;
  deletingErrorToastSet.add(message);
  notification["error"]({ message });
  setTimeout(() => {
    deletingErrorToastSet.delete(message);
  }, 1500);
};

export const PER_PAGE_GET_ALL = 1000000;

export const useFetchPositions = (
  page: number,
  pageSize: number,
  filterParams: any,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.POSITIONS, companyId, page, pageSize, filterParams],
    () =>
      axios({
        method: "get",
        url: SERVICES.POSITIONS,
        params: {
          ...filterParams,
          page,
          per_page: pageSize,
          "q[company_id_eq]": companyId,
        },
      }).then((res) => ({
        data: res.data as Position[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
  );
};

export const useFetchPositionsV2 = (
  page: number,
  pageSize: number,
  filterParams: any,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.POSITIONS, companyId, page, pageSize, filterParams],
    () =>
      axiosInstanceV2({
        method: "get",
        url: SERVICES.POSITIONS,
        params: {
          ...filterParams,
          page,
          per_page: pageSize,
          "q[company_id_eq]": companyId,
        },
      }).then((res) => ({
        data: res.data as Position[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
  );
};

export const useFetchOnePosition = (positionId: any) => {
  return useQuery(
    [QUERY_NAME.POSITION, positionId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.POSITIONS}/${positionId}`,
      }).then((res) => res.data as Position),
    {
      cacheTime: Infinity,
      staleTime: Infinity,
      enabled: !!positionId,
    },
  );
};

export const useDeletePosition = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ positionId, silent }: any) =>
      axios({
        method: "delete",
        url: `${SERVICES.POSITIONS}/${positionId}`,
      }),
    {
      onSuccess(data, variables: any) {
        // Invalidate without auto-refetch to prevent multiple API calls
        queryClient.invalidateQueries([QUERY_NAME.POSITIONS], {
          refetchType: "none",
        });
        // Only show notification if not silent
        if (!variables?.silent) {
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(error: any) {
        const backendMessage = handleMessageError(
          error?.response?.data?.msg || error?.response?.data?.error,
        );
        notifyDeleteErrorOnce(backendMessage || "削除に失敗しました。");
      },
    },
  );
};

export const useDeleteMultiplePositions = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ listIds }: any) => {
      const formData = new FormData();
      listIds.forEach((id: any) => {
        formData.append(`delete_ids[]`, id);
      });
      return axios({
        method: "post",
        data: formData,
        url: `${SERVICES.POSITIONS}/delete_multiple`,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });
    },
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.POSITIONS]);
        notification["success"]({
          message: "保存しました",
        });
      },
      onError(error: any) {
        const backendMessage = handleMessageError(
          error?.response?.data?.msg || error?.response?.data?.error,
        );
        notifyDeleteErrorOnce(backendMessage || "削除に失敗しました。");
      },
    },
  );
};

export const useSubmitPosition = () => {
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  return useMutation(
    ({ payload, positionId, silent }: any) =>
      axios({
        method: positionId ? "patch" : "post",
        url: positionId
          ? `${SERVICES.POSITIONS}/${positionId}`
          : SERVICES.POSITIONS,
        data: { ...payload, company_id: String(companyId) },
        headers: {
          "Content-Type": "application/json",
        },
      }),
    {
      onSuccess(data, variables: any) {
        // Invalidate without auto-refetch to prevent multiple API calls
        // Components will manually refetch when needed
        queryClient.invalidateQueries([QUERY_NAME.POSITIONS], {
          refetchType: "none",
        });
        queryClient.invalidateQueries([QUERY_NAME.POSITION], {
          refetchType: "none",
        });
        // Only show notification if not silent
        if (!variables?.silent) {
          notification["success"]({
            message: "保存しました",
          });
        }
      },
      onError(error: any) {
        const msgError =
          error?.response?.data?.msg || error?.response?.data?.error;
        notification["error"]({
          message: msgError || "保存に失敗しました。",
        });
      },
    },
  );
};

export const useFetchPositionsByCompany = (
  page: number,
  pageSize: number,
  companyId: number,
) => {
  return useQuery([QUERY_NAME.POSITIONS, companyId, page, pageSize], () =>
    axios({
      method: "get",
      url: SERVICES.POSITIONS,
      params: {
        page,
        per_page: pageSize,
        "q[company_id_eq]": companyId,
      },
    }).then((res) => ({
      data: res.data as Position[],
      meta: {
        total: res.headers["x-total"] || 0,
        total_page: res.headers["x-total-pages"] || 0,
      },
    })),
  );
};
